import React, { useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, Progress, Button } from 'reactstrap';
import { useLoading } from '../../../Context/LoadingContext';
import { ThreeCircles } from 'react-loader-spinner';

/**
 * Configuración de UI por fase
 */
const PHASE_CONFIG = {
    processing: {
        icon: 'ri-file-list-3-line',
        iconBg: 'bg-soft-primary',
        iconColor: 'text-primary',
        title: 'Generando Guía de Despacho',
        subtitle: 'Por favor espere mientras procesamos su solicitud',
        progressColor: 'success',
    },
    rollback: {
        icon: 'ri-alert-line',
        iconBg: 'bg-soft-warning',
        iconColor: 'text-warning',
        title: 'Revirtiendo operación...',
        subtitle: 'Se detectó un error, anulando cambios realizados',
        progressColor: 'warning',
    },
    completed: {
        icon: 'ri-checkbox-circle-line',
        iconBg: 'bg-soft-success',
        iconColor: 'text-success',
        title: 'Guía generada exitosamente',
        subtitle: 'El proceso se completó correctamente',
        progressColor: 'success',
    },
    failed: {
        icon: 'ri-close-circle-line',
        iconBg: 'bg-soft-danger',
        iconColor: 'text-danger',
        title: 'Error en la generación',
        subtitle: 'Se realizó rollback de los cambios',
        progressColor: 'danger',
    },
    aborted: {
        icon: 'ri-stop-circle-line',
        iconBg: 'bg-soft-secondary',
        iconColor: 'text-secondary',
        title: 'Operación cancelada',
        subtitle: 'El proceso fue interrumpido',
        progressColor: 'danger',
    },
};

/**
 * Componente de ícono para cada step
 */
const StepIcon = ({ status }) => {
    const iconMap = {
        pending: 'ri-circle-line text-muted',
        done: 'ri-checkbox-circle-fill text-success',
        error: 'ri-close-circle-fill text-danger',
        warning: 'ri-alert-fill text-warning',
        skipped: 'ri-indeterminate-circle-line text-muted',
    };

    if (status === 'loading') {
        return (
            <div 
                className="d-flex align-items-center justify-content-center" 
                style={{ width: '24px', height: '24px', minWidth: '24px' }}
            >
                <div className="spinner-border text-primary" role="status" style={{ width: '20px', height: '20px' }}>
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    const iconClass = iconMap[status] || iconMap.pending;
    return (
        <div 
            className="d-flex align-items-center justify-content-center" 
            style={{ width: '24px', height: '24px', minWidth: '24px' }}
        >
            <i className={`${iconClass}`} style={{ fontSize: '20px' }}></i>
        </div>
    );
};

/**
 * Componente individual de paso
 */
const StepItem = ({ step, isActive }) => {
    const bgClass = isActive ? 'bg-primary-subtle' : '';
    const borderClass = isActive ? 'border-primary' : 'border-transparent';
    
    return (
        <div 
            className={`d-flex align-items-center mb-2 p-3 rounded-2 border ${bgClass} ${borderClass}`}
            style={{ 
                transition: 'all 0.3s ease',
                borderWidth: '2px'
            }}
        >
            <div className="flex-shrink-0 me-3">
                <StepIcon status={step.status} />
            </div>
            <div className="flex-grow-1">
                <p className={`mb-0 ${isActive ? 'fw-semibold' : 'fw-medium'}`}>
                    {step.label}
                </p>
                {step.message && (
                    <p className="mb-0 text-muted small mt-1">{step.message}</p>
                )}
            </div>
        </div>
    );
};

/**
 * Modal de loading con soporte para pasos de progreso y fases
 */
const ModalLoading = () => {
    const { isLoading, steps, phase, summaryMessage, actionButtons, closeLoading } = useLoading();

    // Calcular progreso
    const progress = useMemo(() => {
        if (!steps || steps.length === 0) return { completed: 0, total: 0, percentage: 0 };
        
        const completed = steps.filter(s => s.status === 'done' || s.status === 'skipped').length;
        const total = steps.length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        
        return { completed, total, percentage };
    }, [steps]);

    // Determinar si hay errores
    const hasErrors = useMemo(() => {
        return steps.some(s => s.status === 'error');
    }, [steps]);

    // Determinar el paso activo
    const activeStepId = useMemo(() => {
        const activeStep = steps.find(s => s.status === 'loading');
        return activeStep?.id || null;
    }, [steps]);

    // Detectar index donde comienzan los pasos de rollback (pasos con id que empieza con 'rb_')
    const rollbackStartIndex = useMemo(() => {
        const index = steps.findIndex(s => s.id.startsWith('rb_'));
        return index >= 0 ? index : steps.length;
    }, [steps]);

    // Separar pasos normales y de rollback
    const normalSteps = useMemo(() => steps.slice(0, rollbackStartIndex), [steps, rollbackStartIndex]);
    const rollbackSteps = useMemo(() => steps.slice(rollbackStartIndex), [steps, rollbackStartIndex]);

    // Determinar si el modal debe cerrarse manualmente
    const showCloseButton = ['completed', 'failed', 'aborted'].includes(phase);

    // Configuración de UI según fase
    const phaseConfig = PHASE_CONFIG[phase] || PHASE_CONFIG.processing;

    // Renderizar spinner clásico (backward compatible)
    if (!steps || steps.length === 0) {
        return (
            <Modal isOpen={isLoading} backdrop={'static'} centered className='modal-sm'>
                <ModalHeader className="modal-title fw-bold" id="staticBackdropLabel"></ModalHeader>
                <ModalBody className="text-center p-5 d-flex flex-column align-items-center">
                    <ThreeCircles
                        visible={true}
                        height="50"
                        width="50"
                        color="var(--vz-secondary)"
                        ariaLabel="three-circles-loading"
                        wrapperStyle={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                        wrapperClass=""
                    />
                    <div className="mt-4 text-center">
                        <h3 className="mb-2">Procesando...</h3>
                        <p className="text-muted">Espere mientras procesamos su solicitud.</p>
                    </div>
                </ModalBody>
            </Modal>
        );
    }

    // Renderizar UI con pasos
    return (
        <Modal isOpen={isLoading} backdrop={'static'} centered size="lg">
            <ModalBody className="p-4">
				{/* Header con ícono y título dinámicos según fase */}
				<div className="text-center mb-4">
					<div className="mx-auto mb-3">
						<i className={`${phaseConfig.icon} ${phaseConfig.iconColor}`} style={{ fontSize: '4rem' }}></i>
					</div>
					<h4 className="mb-1">{phaseConfig.title}</h4>
					<p className="text-muted mb-0">{phaseConfig.subtitle}</p>
				</div>

                {/* Barra de progreso */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted small">Progreso</span>
                        <span className="text-muted small fw-semibold">{progress.percentage}%</span>
                    </div>
                    <Progress 
                        value={progress.percentage} 
                        color={hasErrors ? 'danger' : phaseConfig.progressColor}
                        className="progress-sm"
                        animated={phase === 'processing'}
                    />
                </div>

                {/* Lista de pasos normales */}
                <div className="mb-3">
                    {normalSteps.map(step => (
                        <StepItem 
                            key={step.id} 
                            step={step} 
                            isActive={step.id === activeStepId}
                        />
                    ))}
                </div>

                {/* Separador y pasos de rollback si existen */}
                {rollbackSteps.length > 0 && (
                    <>
                        <div className="d-flex align-items-center my-3">
                            <hr className="flex-grow-1" />
                            <span className="px-3 text-warning small fw-semibold">
                                <i className="ri-alert-line me-1"></i>
                                Proceso de anulación
                            </span>
                            <hr className="flex-grow-1" />
                        </div>
                        <div className="mb-3">
                            {rollbackSteps.map(step => (
                                <StepItem 
                                    key={step.id} 
                                    step={step} 
                                    isActive={step.id === activeStepId}
                                />
                            ))}
                        </div>
                    </>
                )}

                {/* Mensaje de resumen o estado */}
                {summaryMessage ? (
                    <div className={`alert alert-${
                        phase === 'completed' ? 'success' :
                        phase === 'failed' || phase === 'aborted' ? 'danger' :
                        hasErrors ? 'danger' : 'info'
                    } border-0 mb-0`} role="alert">
                        <div className="d-flex align-items-start">
                            <i className={`${
                                phase === 'completed' ? 'ri-checkbox-circle-line' :
                                phase === 'failed' || phase === 'aborted' ? 'ri-error-warning-line' :
                                'ri-information-line'
                            } fs-18 me-2 flex-shrink-0 mt-1`}></i>
                            <div className="flex-grow-1">
                                <p className="mb-0">{summaryMessage}</p>
                            </div>
                        </div>
                    </div>
                ) : !showCloseButton && (
                    <div className="text-center py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                        <span className="text-muted small">Procesando...</span>
                    </div>
                )}

                {/* Botones de acción o botón Cerrar */}
                {(showCloseButton || (actionButtons && actionButtons.length > 0)) && (
                    <div className="text-center mt-3 d-flex justify-content-center gap-2">
                        {actionButtons && actionButtons.length > 0 ? (
                            actionButtons.map((btn, idx) => (
                                <Button 
                                    key={idx}
                                    color={btn.color || 'primary'}
                                    onClick={btn.onClick}
                                    className="btn-sm px-4"
                                >
                                    {btn.icon && <i className={`${btn.icon} me-1`}></i>}
                                    {btn.label}
                                </Button>
                            ))
                        ) : showCloseButton && (
                            <Button 
                                color={phase === 'completed' ? 'success' : 'secondary'}
                                onClick={closeLoading}
                                className="btn-sm px-4"
                            >
                                <i className="ri-close-line me-1"></i>
                                Cerrar
                            </Button>
                        )}
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

export default ModalLoading;
